import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

// Single-select searchable dropdown
export function SearchableSelect({ value, onChange, options, placeholder = "Select", testid, allowClear = true }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full justify-between rounded-xl border-emerald-900/15 bg-white font-normal text-emerald-950 hover:bg-mint"
        >
          <span className={cn("truncate", !value && "text-slate-400")}>{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {allowClear && value && (
                <CommandItem value="__clear__" onSelect={() => { onChange(""); setOpen(false); }} className="text-slate-500">
                  <X className="mr-2 h-4 w-4" /> Clear selection
                </CommandItem>
              )}
              {options.map((o) => (
                <CommandItem
                  key={o}
                  value={o}
                  data-testid={testid ? `${testid}-opt-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}
                  onSelect={() => { onChange(o); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === o ? "opacity-100" : "opacity-0")} />
                  {o}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Multi-select searchable dropdown. value is an array of strings.
export function MultiSearchableSelect({ value = [], onChange, options, placeholder = "Select", testid }) {
  const [open, setOpen] = useState(false);
  const selected = Array.isArray(value) ? value : [];
  const toggle = (o) => {
    onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
  };
  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            data-testid={testid}
            className="w-full justify-between rounded-xl border-emerald-900/15 bg-white font-normal text-emerald-950 hover:bg-mint"
          >
            <span className={cn("truncate", selected.length === 0 && "text-slate-400")}>
              {selected.length === 0 ? placeholder : `${selected.length} selected`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search…" />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((o) => {
                  const active = selected.includes(o);
                  return (
                    <CommandItem
                      key={o}
                      value={o}
                      data-testid={testid ? `${testid}-opt-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}
                      onSelect={() => toggle(o)}
                    >
                      <Check className={cn("mr-2 h-4 w-4", active ? "opacity-100" : "opacity-0")} />
                      {o}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={testid ? `${testid}-chips` : undefined}>
          {selected.map((o) => (
            <span key={o} className="inline-flex items-center gap-1 rounded-full bg-mint px-3 py-1 text-xs font-semibold text-emerald-900 ring-1 ring-emerald-900/10">
              {o}
              <button type="button" onClick={() => toggle(o)} data-testid={testid ? `${testid}-remove-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined} className="rounded-full hover:text-emerald-600">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
